﻿import { openOverlay } from './SearchOverlay'

export const initialise = () => {
    const header = document.querySelector<HTMLElement>('header#header')
    const burgerMenu = document.querySelector<HTMLDialogElement>('#burger-navigation')
    if (!header || !burgerMenu) return

    removeTabIndex(header, burgerMenu)
    stickyHeader(header)
    navigationMenus(header)
    burgerToggle(header, burgerMenu)
    burgerNavigationMenus(burgerMenu)
}

const removeTabIndex = (header: HTMLElement, burgerMenu: HTMLDialogElement) => {
    const navigationLinks = [
        ...header.querySelectorAll<HTMLElement>('*[tabindex]'),
        ...burgerMenu.querySelectorAll<HTMLElement>('*[tabindex]')
    ]

    navigationLinks.forEach((link) => {
        link.removeAttribute('tabindex')
    })
}

const stickyHeader = (header: HTMLElement) => {
    const scrollUpClass: string = 'header--scroll-up'
    const scrollDownClass: string = 'header--scroll-down'
    let lastScrollPosition: number = 0

    const headerHeight: number = header.offsetHeight

    window.addEventListener('scroll', () => {
        const currentScrollPosition: number = window.scrollY

        if (currentScrollPosition <= headerHeight) {
            header.classList.remove(scrollUpClass, scrollDownClass)
        } else if (currentScrollPosition > lastScrollPosition) {
            header.classList.add(scrollDownClass)
            header.classList.remove(scrollUpClass)
        } else {
            header.classList.add(scrollUpClass)
            header.classList.remove(scrollDownClass)
        }
        lastScrollPosition = currentScrollPosition
    })
}

const openNavigationMenu = (item: HTMLElement, menu: HTMLElement, keyboardNavigation: boolean = false) => {
    if (item.classList.contains('open')) return
    item.classList.add('open')
    item.setAttribute('aria-expanded', 'true')
    menu.setAttribute('aria-hidden', 'false')
    if (!keyboardNavigation) return
    menu.querySelector<HTMLAnchorElement>('a')?.focus()
}

const closeNavigationMenu = (item: HTMLElement, menu: HTMLElement, keyboardNavigation: boolean = false) => {
    if (!item.classList.contains('open')) return
    item.classList.remove('open')
    item.setAttribute('aria-expanded', 'false')
    menu.setAttribute('aria-hidden', 'true')
    const childItems = menu.querySelectorAll<HTMLElement>('.hasChildren')
    childItems.forEach((childItem: HTMLElement) => {
        const childMenu = childItem.querySelector<HTMLElement>('.navList')
        if (!childMenu) return
        closeNavigationMenu(childItem, childMenu, false)
    })
    if (!keyboardNavigation) return
    item.querySelector<HTMLButtonElement>('.icon')?.focus()
}

const toggleNavigationMenu = (item: HTMLElement, menu: HTMLElement, keyboardNavigation: boolean = false) => {
    item.classList.toggle('open')
    item.setAttribute('aria-expanded', item.classList.contains('open') ? 'true' : 'false')
    menu.setAttribute('aria-hidden', item.classList.contains('open') ? 'false' : 'true')
    if (!keyboardNavigation) return

    if (item.classList.contains('open')) {
        menu.querySelector<HTMLAnchorElement>('a')?.focus()
    } else {
        item.querySelector<HTMLButtonElement>('.icon')?.focus()
    }
}

const navigationMenus = (header: HTMLElement) => {
    const items = header.querySelectorAll<HTMLElement>('.mainNav-widget .hasChildren')

    items.forEach((item: HTMLElement) => {
        const icon = item.querySelector<HTMLButtonElement>('.icon')
        const menu = item.querySelector<HTMLElement>('.navList')
        const parent = item.parentElement?.parentElement?.classList.contains('hasChildren') ? item.parentElement?.parentElement : null
        const childrenMenus = item.querySelectorAll<HTMLElement>('.navList .hasChildren')
        if (!menu || !icon) return

        closeNavigationMenu(item, menu)

        icon.addEventListener('click', (event: MouseEvent) => {
            const relatedTarget = event.relatedTarget as HTMLElement | null
            if (relatedTarget && (menu.contains(relatedTarget) || (parent && parent.contains(relatedTarget)))) return
            toggleNavigationMenu(item, menu)
        })

        icon.addEventListener('keydown', (event: KeyboardEvent) => {
            if (event.key === ' ' || event.key === 'Enter') {
                event.preventDefault()
                toggleNavigationMenu(item, menu, true)
            }
        })

        menu.addEventListener('focusout', (event: FocusEvent) => {
            const relatedTarget = event.relatedTarget as HTMLElement | null
            if (relatedTarget && (menu.contains(relatedTarget))) return
            closeNavigationMenu(item, menu, true)
        })

        item.addEventListener('mouseenter', (event: MouseEvent) => {
            openNavigationMenu(item, menu)
        })

        item.addEventListener('mouseleave', (event: MouseEvent) => {
            const relatedTarget = event.relatedTarget as HTMLElement | null
            let isChild = false
            for (const childMenu of childrenMenus) {
                if (relatedTarget && childMenu.contains(relatedTarget)) {
                    isChild = true
                    break
                }
            }
            if (isChild) return

            if (parent && relatedTarget && parent.contains(relatedTarget)) {
                closeNavigationMenu(item, menu)
                return
            }

            if (relatedTarget && menu.contains(relatedTarget)) {
                return
            }

            closeNavigationMenu(item, menu)
        })
    })
}

const burgerOpen = (dialog: HTMLDialogElement, burgerMenuToggles: Array<Element>) => {
    dialog.showModal()
    burgerMenuToggles.forEach((toggle) => {
        toggle.setAttribute('aria-expanded', 'true')
        dialog.setAttribute('aria-hidden', 'false')
    })
}

const burgerClose = (dialog: HTMLDialogElement, burgerMenuToggles: Array<Element>) => {
    dialog.close()
    burgerMenuToggles.forEach((toggle) => {
        toggle.setAttribute('aria-expanded', 'false')
        dialog.setAttribute('aria-hidden', 'true')
    })
}

const burgerToggle = (header: HTMLElement, burgerMenu: HTMLDialogElement) => {
    const burgerMenuToggles = [
        ...header.querySelectorAll('.header__action-burger-toggle'),
        ...burgerMenu.querySelectorAll('.header__action-burger-toggle')
    ]

    const dialog: HTMLDialogElement | null = document.querySelector('.header__container-burger-navigation')
    if (!dialog) return
    dialog.setAttribute('aria-hidden', 'true')

    burgerMenuToggles.forEach((toggle) => {
        toggle.setAttribute('aria-expanded', 'false')
        toggle.setAttribute('aria-controls', 'burger-navigation')
        
        toggle.addEventListener('click', () => {
            document.body.classList.toggle('header--open')

            if (dialog.open) {
                burgerClose(dialog, burgerMenuToggles)
                return
            }

            burgerOpen(dialog, burgerMenuToggles)

            const menuItems = burgerMenu?.querySelectorAll<HTMLElement>('.hasChildren')

            menuItems.forEach((menuItem: HTMLUListElement) => {
                const menu = menuItem.querySelector<HTMLElement>('.navList')
                if (!menu) return

                closeNavigationMenu(menuItem, menu)
            })

            const firstLink: HTMLAnchorElement | null = dialog.querySelector('a')
            if (firstLink) {
                setTimeout(() => {
                    firstLink.focus()
                }, 100)
            }

            dialog.addEventListener('close', function closeDialog() {
                burgerClose(dialog, burgerMenuToggles)

                setTimeout(() => {
                    const items = burgerMenu?.querySelectorAll<HTMLElement>('.mainNav-widget .hasChildren')
                    items.forEach((item: HTMLUListElement) => {
                        const menu = item.querySelector<HTMLElement>('.navList')
                        if (!menu) return
                        closeNavigationMenu(item, menu)
                    })
                    dialog.removeEventListener('close', closeDialog)
                }, 300);
            })

            const overlayOpenButton = document.querySelector<HTMLButtonElement>('#burger-navigation .header__button-open-dialog-search-overlay')
            overlayOpenButton?.addEventListener('click', () => {
                burgerClose(dialog, burgerMenuToggles)
                
                const overlay = document.querySelector<HTMLDialogElement>('#dialog-search-overlay')
                if (!overlay) return

                openOverlay(overlay)
            })

            window.addEventListener('resize', function closeDialogResize() {
                if (window.innerWidth >= 991) {
                    burgerClose(dialog, burgerMenuToggles)
                    window.removeEventListener('resize', closeDialogResize)
                }
            })
        })
    })
}

const burgerNavigationMenus = (burgerMenu: HTMLDialogElement) => {
    const items = burgerMenu.querySelectorAll<HTMLElement>('.mainNav-widget .hasChildren')

    items.forEach((item: HTMLUListElement) => {
        const icon = item.querySelector<HTMLButtonElement>('.icon')
        const menu = item.querySelector<HTMLElement>('.navList')
        const parent = item.parentElement?.parentElement?.classList.contains('hasChildren') ? item.parentElement?.parentElement : null
        if (!menu || !icon) return
        const menuClose = menu.querySelector<HTMLButtonElement>('.nav-back-btn')
        if (!menuClose) return

        closeNavigationMenu(item, menu)

        icon.addEventListener('click', (event: MouseEvent) => {
            const relatedTarget = event?.relatedTarget as HTMLElement | null
            if (relatedTarget && (menu.contains(relatedTarget) || (parent && parent.contains(relatedTarget)))) return
            openNavigationMenu(item, menu)
        })

        icon.addEventListener('keydown', (event: KeyboardEvent) => {
            if (event.key === ' ' || event.key === 'Enter') {
                event.preventDefault()
                openNavigationMenu(item, menu, true)
            }
        })

        menuClose.addEventListener('click', () => {
            closeNavigationMenu(item, menu)
        })
    })
}